import { alpha } from '@mui/material/styles';
import type { Components, Theme } from '@mui/material/styles';

export const inputComponents: Components<Theme> = {
    MuiFormControl: {
        styleOverrides: {
            root: ({ theme }) => ({
                /*
                 * MUI automatically shrinks labels when a startAdornment is
                 * present. We intentionally override that behavior in our
                 * shared inputs, so an empty field can keep its label inside.
                 *
                 * While the label is inside, shift it by exactly one leading
                 * adornment slot. Using the independent CSS `translate`
                 * property preserves MUI's own vertical transform/animation.
                 * Once the label shrinks, reset this extra translation so it
                 * lands in the normal outlined-label position. This works for
                 * icons, action buttons and text adornments without hardcoding
                 * a second full transform.
                 */
                '&:has(.MuiInputBase-adornedStart) .MuiInputLabel-outlined': {
                    transition: theme.transitions.create(
                        ['color', 'transform', 'translate'],
                        { duration: 150 },
                    ),
                },
                '&:has(.MuiInputBase-adornedStart) .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)': {
                    translate: '-50px 0',
                },
                '&:has(.MuiInputBase-adornedStart) .MuiInputLabel-outlined.MuiInputLabel-shrink': {
                    translate: '0 0',
                },
            }),
        },
    },

    MuiFormLabel: {
        styleOverrides: {
            root: ({ theme }) => ({
                color: theme.palette.text.secondary,
                lineHeight: 1.4,
                paddingInline: 2,
                fontSize: 13,
                '&.MuiInputLabel-outlined.MuiInputLabel-shrink': {
                    // MUI shrinks outlined labels with scale(0.75), while the
                    // notch legend keeps its real 5px side padding. Compensate
                    // for that scale so the label footprint and the notch
                    // (including the focus glow cut-out) end at the exact same
                    // pixels on both sides.
                    paddingInline: '6.6667px',
                    // Keep the floating label surface-neutral. The outlined
                    // fieldset already owns the notch, so painting a paper
                    // rectangle behind the label creates a visible patch when
                    // the field lives on background.default, cards or dialogs.
                    backgroundColor: 'transparent',
                },
                '&.Mui-focused': {
                    color:
                        theme.palette.mode === 'dark'
                            ? theme.palette.primary.light
                            : theme.palette.primary.main,
                },
                '&.Mui-error': {
                    color:
                        theme.palette.mode === 'dark'
                            ? theme.palette.error.light
                            : theme.palette.error.main,
                },
            }),
        },
    },

    MuiInputBase: {
        styleOverrides: {
            root: ({ theme }) => ({
                borderRadius: theme.shape.borderRadius,
                padding: 0,
                '&.Mui-disabled': {
                    cursor: 'not-allowed',
                },
                '&.Mui-disabled .MuiInputBase-input': {
                    color: theme.palette.text.disabled,
                    WebkitTextFillColor: theme.palette.text.disabled,
                },
                '&.MuiInputBase-multiline .MuiInputBase-inputMultiline': {
                    padding: 16,
                },
            }),
            input: {
                paddingTop: 13,
                paddingBottom: 13,
            },
        },
    },

    MuiOutlinedInput: {
        styleOverrides: {
            root: ({ theme }) => ({
                // Keep the root unclipped so the focus glow can be rendered by
                // the real notched fieldset. Unlike a root box-shadow, this makes
                // the glow respect the legend gap exactly.
                overflow: 'visible',
                minHeight: 46,
                // Outlined fields inherit the surface they are placed on.
                // This keeps inputs visually consistent on page, card and
                // dialog backgrounds and avoids a fill-color jump on hover or
                // focus. Components that intentionally need a filled surface
                // can still override this locally.
                backgroundColor: 'transparent',
                transition: theme.transitions.create(['border-color'], {
                    duration: 150,
                }),
                '& .MuiOutlinedInput-notchedOutline': {
                    borderWidth: 1,
                    borderColor: theme.palette.border.main,
                    transition: theme.transitions.create(
                        ['border-color', 'filter'],
                        { duration: 150 },
                    ),
                },
                '& .MuiOutlinedInput-notchedOutline legend > span': {
                    paddingInline: 5,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderWidth: 1,
                    borderColor: theme.palette.border.strong,
                },
                '&.MuiInputBase-adornedStart, &.MuiInputBase-adornedEnd': {
                    paddingInline: '0 !important',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: 1,
                    borderColor:
                        theme.palette.mode === 'dark'
                            ? theme.palette.primary.light
                            : theme.palette.primary.main,
                    // Render the glow from the notched fieldset itself. `filter`
                    // follows the actual painted border (including the legend gap),
                    // while a root box-shadow always paints one continuous rectangle.
                    filter: `drop-shadow(0 0 3px ${alpha(
                        theme.palette.primary.main,
                        theme.palette.mode === 'dark' ? 0.34 : 0.16,
                    )})`,
                },
                '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                    borderWidth: 1,
                    borderColor: theme.palette.error.main,
                },
                '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    filter: `drop-shadow(0 0 3px ${alpha(
                        theme.palette.error.main,
                        theme.palette.mode === 'dark' ? 0.32 : 0.16,
                    )})`,
                },
                '&.Mui-error .MuiInputAdornment-root': {
                    color: theme.palette.error.main,
                },
            }),
            input: {
                // Logical padding keeps the caret/text away from adornment
                // dividers in both RTL and LTR fields without direction hacks.
                paddingInline: 14,
            },
        },
    },

    MuiInputAdornment: {
        styleOverrides: {
            root: ({ theme }) => ({
                boxSizing: 'border-box',
                alignSelf: 'stretch',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                height: 'auto',
                maxHeight: 'none',
                margin: '0 !important',
                paddingInline: 12,
                color: theme.palette.text.secondary,
                border: 'none',

                // Action adornments (copy / eye / clear / etc.) must occupy the
                // exact same box on both sides of the input. MUI adds its own
                // start/end root padding; MuiOutlinedInput above removes it.
                '&:has(.MuiIconButton-root)': {
                    width: 50,
                    minWidth: 50,
                    padding: 0,
                },
                '& .MuiIconButton-root': {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                    minHeight: 46,
                    margin: '0 !important',
                    padding: 0,
                    borderRadius: 0,
                },
                '& .MuiIconButton-root > i': {
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 18,
                    minWidth: 18,
                    lineHeight: 1,
                    textAlign: 'center',
                },
                '&.MuiInputAdornment-positionStart': {
                    borderInlineEnd: `1px solid ${theme.palette.border.light}`,
                    borderStartStartRadius: 'inherit',
                    borderEndStartRadius: 'inherit',
                    overflow: 'hidden',
                },
                '&.MuiInputAdornment-positionEnd': {
                    borderInlineStart: `1px solid ${theme.palette.border.light}`,
                    borderStartEndRadius: 'inherit',
                    borderEndEndRadius: 'inherit',
                    overflow: 'hidden',
                },
            }),
        },
    },

    MuiInput: {
        styleOverrides: {
            root: {
                '&.MuiInput-underline-none:before': {
                    borderBottom: 'none',
                },
                '&.MuiInput-underline-none:after': {
                    borderBottom: 'none',
                },
            },
        },
    },

    MuiSelect: {
        styleOverrides: {
            icon: ({ theme }) => ({
                color: theme.palette.text.secondary,
            }),
        },
    },

    MuiCheckbox: {
        styleOverrides: {
            root: ({ theme }) => ({
                borderRadius: 8,
                padding: 7,
                '&.MuiCheckbox-indeterminate': {
                    color: theme.palette.primary.main,
                },
            }),
        },
    },

    MuiRadio: {
        styleOverrides: {
            root: {
                padding: 7,
            },
        },
    },

    MuiFormHelperText: {
        styleOverrides: {
            root: {
                marginInline: 4,
                marginTop: 5,
                fontSize: 11,
            },
        },
    },

    MuiSwitch: {
        styleOverrides: {
            root: {
                width: 46,
                height: 28,
                padding: 4,
            },
            switchBase: ({ theme }) => ({
                padding: 6,
                '&.Mui-checked': {
                    color: theme.palette.common.white,
                    transform: 'translateX(18px)',
                    '& + .MuiSwitch-track': {
                        opacity: 1,
                        backgroundColor: theme.palette.primary.main,
                    },
                },
            }),
            thumb: ({ theme }) => ({
                width: 16,
                height: 16,
                boxShadow: `0 2px 7px ${alpha(theme.palette.common.black, 0.2)}`,
            }),
            track: ({ theme }) => ({
                opacity: 1,
                borderRadius: 99,
                backgroundColor: theme.palette.border.strong,
            }),
        },
    },

    MuiSlider: {
        styleOverrides: {
            root: ({ theme }) => ({
                color: theme.palette.primary.main,
                height: 5,
            }),
            rail: ({ theme }) => ({
                opacity: 1,
                backgroundColor: theme.palette.border.main,
            }),
            track: {
                border: 0,
            },
            thumb: ({ theme }) => ({
                width: 17,
                height: 17,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.22)}`,
                '&:before': {
                    boxShadow: 'none',
                },
            }),
        },
    },

};
